import { createContext, useState, useEffect, useRef, useCallback, useContext } from 'react';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function decodeJwtPayload(token) {
  const b64    = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '=='.slice(0, (4 - b64.length % 4) % 4);
  return JSON.parse(atob(padded));
}

export const AuthProvider = ({ children }) => {
  const [user,    setUser]    = useState(null);
  const [token,   setToken]   = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Set by loginWithToken so the useEffect knows to skip re-decoding
  const skipDecode = useRef(false);

  useEffect(() => {
    if (token) {
      if (skipDecode.current) {
        // loginWithToken already decoded and set the user — don't re-run
        skipDecode.current = false;
      } else {
        // Page-load: restore user from token stored in localStorage
        try {
          const payload = decodeJwtPayload(token);
          setUser({ id: payload.id, username: payload.username, role: payload.role });
        } catch (e) {
          localStorage.removeItem('token');
          setToken(null);
        }
      }
    }
    setLoading(false);
  }, [token]);

  const login = async (username, password) => {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    if (!response.ok) throw new Error('Login failed');
    const data = await response.json();
    localStorage.setItem('token', data.token);
    setToken(data.token);
    setUser(data.user);
  };

  const loginWithToken = useCallback((rawToken) => {
    try {
      const payload = decodeJwtPayload(rawToken);
      localStorage.setItem('token', rawToken);
      skipDecode.current = true; // tell useEffect to skip re-decode
      setToken(rawToken);
      setUser({ id: payload.id, username: payload.username, role: payload.role });
    } catch {
      throw new Error('Invalid token');
    }
  }, []); // stable reference — setToken/setUser are guaranteed stable by React

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, loginWithToken, logout, isAuthenticated: !!token, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
